import { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, User, Phone, Lock, Building } from 'lucide-react';
import { Input } from '../../common/components/inputs';
import { Button } from '../../common/components/buttons';
import { ErrorMessage } from '../../common/components/feedback';
import { AuthCard, AuthLayout, PasswordStrengthIndicator } from '../ui';
import { usePhoneMask, useFormValidation } from '../hooks';
import { register } from '../../../services/authService';

/**
 * RegisterPage - Página de cadastro de novos usuários
 */
export const RegisterPage = () => {
  const navigate = useNavigate();
  const { handlePhoneChange } = usePhoneMask();
  const {
    validateEmail,
    validatePassword,
    validateConfirmPassword,
    validateName,
    validatePhone,
    checkPasswordRequirements,
    calculatePasswordStrength,
  } = useFormValidation();

  // Form state
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // UI state
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showPasswordIndicator, setShowPasswordIndicator] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setGeneralError('');
    setErrors({});

    // Validação
    const validationErrors: Record<string, string> = {};

    const emailError = validateEmail(email);
    if (emailError) validationErrors.email = emailError;

    const nameError = validateName(name);
    if (nameError) validationErrors.name = nameError;

    // Validar nome da organização
    if (!organizationName.trim()) {
      validationErrors.organizationName = 'Nome da organização é obrigatório';
    } else if (organizationName.trim().length < 2) {
      validationErrors.organizationName = 'Nome da organização deve ter pelo menos 2 caracteres';
    } else if (organizationName.trim().length > 255) {
      validationErrors.organizationName = 'Nome da organização deve ter no máximo 255 caracteres';
    }

    const phoneError = validatePhone(phone);
    if (phoneError) validationErrors.phone = phoneError;

    const passwordError = validatePassword(password);
    if (passwordError) validationErrors.password = passwordError;

    const confirmPasswordError = validateConfirmPassword(password, confirmPassword);
    if (confirmPasswordError) validationErrors.confirmPassword = confirmPasswordError;

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    // Submit
    setIsLoading(true);
    try {
      await register({
        email,
        name,
        password,
        phone: phone.replace(/\D/g, ''), // Remove formatação
        organization_name: organizationName.trim(), // Nome da organização para criar workspace
      });
      localStorage.setItem('pendingVerificationEmail', email);
      navigate('/verify-email', { state: { email } });
    } catch (err) {
      setGeneralError(err instanceof Error ? err.message : 'Erro no cadastro');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate password requirements and strength in real-time
  const passwordRequirements = checkPasswordRequirements(password);
  const passwordStrength = calculatePasswordStrength(password);

  return (
    <AuthLayout>
      <AuthCard title="Criar conta" subtitle="Comece sua jornada com a TAIS">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Linha 1: Email e Nome completo */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="E-mail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={errors.email}
              icon={Mail}
              required
            />

            <Input
              label="Nome completo"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={errors.name}
              icon={User}
              required
            />
          </div>

          {/* Linha 2: Nome da Organização e Telefone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input
              label="Nome da Organização"
              type="text"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              error={errors.organizationName}
              icon={Building}
              placeholder="Ex: Minha Empresa Ltda"
              required
            />

            <Input
              label="Telefone"
              type="tel"
              value={phone}
              onChange={(e) => {
                const formatted = handlePhoneChange(e);
                setPhone(formatted.target.value);
              }}
              error={errors.phone}
              icon={Phone}
              required
            />
          </div>

          {/* Linha 3: Senha e Confirmar senha */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="relative">
              <Input
                label="Senha"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onFocus={() => setShowPasswordIndicator(true)}
                onBlur={() => setShowPasswordIndicator(false)}
                error={errors.password}
                icon={Lock}
                showPasswordToggle
                required
              />

              {/* Password Strength Indicator - Popover à esquerda */}
              {showPasswordIndicator && (
                <div className="absolute right-full top-0 mr-4 z-50 hidden lg:block animate-fadeIn">
                  <PasswordStrengthIndicator
                    password={password}
                    requirements={passwordRequirements}
                    strength={passwordStrength}
                  />
                </div>
              )}
            </div>

            <Input
              label="Confirmar senha"
              type="password"
              placeholder="••••••••"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              error={errors.confirmPassword}
              icon={Lock}
              showPasswordToggle
              required
            />
          </div>

          <ErrorMessage>{generalError}</ErrorMessage>

          <Button type="submit" variant="primary" fullWidth loading={isLoading}>
            Cadastrar
          </Button>

          <p className="text-center text-[#CCCCCC] text-sm">
            Já possui conta?{' '}
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-[#F4CC04] hover:underline font-medium"
            >
              Faça login
            </button>
          </p>
        </form>
      </AuthCard>
    </AuthLayout>
  );
};
